import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Slide,
    TextField,
    MenuItem,
    Grid,
    Autocomplete,
    FormLabel
} from "@mui/material";
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { ShowAlert } from "../../Component/ShowAlert";
import ExchanageRateService from "../../Services/ExchnageRateService";
import { SlideUpTransition } from "../../Component/Reusables/Transitions";
import { convertToNumber } from "../../Component/config";
import InputTextField from "../../Component/Reusables/InputTextField";

// Interface for Currency data
interface Currency {
    currencyId: string;
    currency: string;
    country: string;
    currencySymbol: string;
    currencyEntity: string;
}

// Interface for props
interface AddCurrencyPairsProps {
    open: boolean;
    accessToken: string;
    onClose?: (state: boolean, action: string) => void;
    refreshData: () => void;
}

// Interface for state
interface AddCurrencyPairsState {
    fromCurrency: string;
    toCurrency: string;
    exchangeRate: string;
    markupPercentage: string;
    platformFeePercentage: string;
    platformFee: string;
    accessToken: string;
    exchangeRateError: string;
    markupPercentageError: string;
    platformFeePercentageError: string;
    platformFeeError: string;
    fromCurrencyError: string;
    toCurrencyError: string;
    fromCurrenciesList: Currency[];
    toCurrenciesList: Currency[];
    loading: boolean;
    fromCurrencyObj: Currency | null;  // New field
    toCurrencyObj: Currency | null;
    checkedFields: {
        isMarkupActive: boolean,
        isPlatformPercentActive: boolean,
        isPlatformFeeActive: boolean,
    },
}

// Main class for AddCurrencyPairs component
class AddCurrencyPairs extends Component<AddCurrencyPairsProps, AddCurrencyPairsState> {
    constructor(props: AddCurrencyPairsProps) {
        super(props);
        this.state = {
            fromCurrency: "",
            toCurrency: "",
            exchangeRate: "",
            markupPercentage: "",
            platformFeePercentage: "",
            platformFee: "",
            accessToken: "",
            exchangeRateError: "",
            markupPercentageError: "",
            platformFeePercentageError: "",
            platformFeeError: "",
            fromCurrencyError: "",
            toCurrencyError: "",
            fromCurrenciesList: [],
            toCurrenciesList: [],
            loading: false,
            fromCurrencyObj: null,  // Initialize as null
            toCurrencyObj: null,
            checkedFields: {
                isMarkupActive: true,
                isPlatformPercentActive: true,
                isPlatformFeeActive: true
            },
        };
    }

    // Get access token and fetch from currencies
    async componentDidMount() {
        const accesstoken_ = await loadFromLocalStorage('accessToken');

        if (accesstoken_) {
            this.setState({
                accessToken: accesstoken_.toString(),
                loading: true
            }, async () => {
                try {
                    const fromCurrencies = await this.fetchFromCurrencies();
                    this.setState({
                        fromCurrenciesList: fromCurrencies,
                        loading: false
                    });
                } catch (error) {
                    this.setState({
                        loading: false,
                        fromCurrencyError: "Failed to load currencies"
                    });
                    console.error("Failed to fetch from currencies:", error);
                    this.props.onClose?.(true, "Cancel");
                }
            });
        } else {
            localStorage.clear();
            window.location.href = '/';
        }
    }

    // API call to fetch from currencies
    private fetchFromCurrencies = async (): Promise<Currency[]> => {
        try {
            const response = await ExchanageRateService.getFromCurrency(this.state.accessToken);
            if (response.ok) {
                const result = await response.json();
                return result;
            }
            throw new Error("Failed to fetch from currencies");
        } catch (error) {
            console.error("Error fetching from currencies:", error);
            throw error;
        }
    }

    // API call to fetch to currencies based on from currency
    private fetchToCurrencies = async (fromCurrency: string): Promise<Currency[]> => {
        const postData = {
            currencyId: fromCurrency
        }

        try {
            const response = await ExchanageRateService.getToCurrency(this.state.accessToken, postData);
            if (response.ok) {
                const result = await response.json();
                return result;
            }
            throw new Error("Failed to fetch to currencies");
        } catch (error) {
            console.error("Error fetching to currencies:", error);
            throw error;
        }
    }

    // Handle from currency change
    handleFromCurrencyChange = async (currencyId: string) => {
        this.setState({
            fromCurrency: currencyId,
            toCurrency: "",
            toCurrenciesList: [],
            fromCurrencyError: "",
            toCurrencyError: "",
            loading: true
        }, async () => {
            try {
                const toCurrencies = await this.fetchToCurrencies(currencyId);
                this.setState({
                    toCurrenciesList: toCurrencies,
                    loading: false
                });
            } catch (error) {
                this.setState({
                    loading: false,
                    toCurrencyError: "Failed to load target currencies"
                });
                this.props.onClose?.(true, "Cancel");
                console.error("Failed to fetch to currencies:", error);
            }
        });
    };

    // Handle field changes
    handleChange = (field: keyof AddCurrencyPairsState, value: string): void => {
        if (field === 'fromCurrency') {
            const selectedCurrencyObj = this.state.fromCurrenciesList.find(
                (item) => item.currencyId === value
            );
            if (selectedCurrencyObj) {
                this.setState({
                    fromCurrencyObj: selectedCurrencyObj
                }, () => {
                    this.handleFromCurrencyChange(selectedCurrencyObj.currencyId);
                });
            }
            return;
        }

        if (field === 'toCurrency') {
            const selectedCurrencyObj = this.state.toCurrenciesList.find(
                (item) => item.currencyId === value
            );
            if (selectedCurrencyObj) {
                this.setState({
                    toCurrencyObj: selectedCurrencyObj,
                    toCurrency: value
                });
            }
            return;
        }

        // Clear any existing error when field changes
        this.setState({
            [`${field}Error`]: ""
        } as any);

        // Validate numeric fields
        if (['exchangeRate', 'markupPercentage', 'platformFeePercentage', 'platformFee'].includes(field)) {
            const sanitizedValue = value.replace(/[^0-9.]/g, "");
            const decimalCount = (sanitizedValue.match(/\./g) || []).length;

            if (decimalCount > 1) return;

            this.setState({ [field]: sanitizedValue } as any);
        } else {
            this.setState({ [field]: value } as any);
        }
    };

    // Validate all fields before submission
    validateFields = () => {
        let isValid = true;
        const errors = {
            fromCurrencyError: "",
            toCurrencyError: "",
            exchangeRateError: "",
            markupPercentageError: "",
            platformFeePercentageError: "",
            platformFeeError: ""
        };

        const {
            fromCurrencyObj,
            toCurrencyObj,
            exchangeRate,
            markupPercentage,
            platformFeePercentage,
            platformFee,
            checkedFields
        } = this.state;

        if (!fromCurrencyObj?.currency) {
            errors.fromCurrencyError = "Source currency is required";
            isValid = false;
        }

        if (!toCurrencyObj?.currency) {
            errors.toCurrencyError = "Target currency is required";
            isValid = false;
        }

        if (!exchangeRate || isNaN(+exchangeRate) || +exchangeRate <= 0) {
            errors.exchangeRateError = "Valid exchange rate is required";
            isValid = false;
        }

        if (checkedFields.isMarkupActive && (!markupPercentage || isNaN(+markupPercentage))) {
            errors.markupPercentageError = "Valid markup percentage is required";
            isValid = false;
        }

        if (checkedFields.isPlatformPercentActive && (!platformFeePercentage || isNaN(+platformFeePercentage))) {
            errors.platformFeePercentageError = "Valid platform fee percentage is required";
            isValid = false;
        }

        if (checkedFields.isPlatformFeeActive && (!platformFee || isNaN(+platformFee))) {
            errors.platformFeeError = "Valid platform fee is required";
            isValid = false;
        }

        this.setState(errors);
        return isValid;
    };

    // Method on save button click api call...
    private handleDialogAction = async (action: string) => {
        if (action === "ok") {
            if (!this.validateFields()) {
                return;
            }

            const postData = {
                fromCurrency: this.state.fromCurrencyObj?.currency,
                toCurrency: this.state.toCurrencyObj?.currency,
                exchangeRate: convertToNumber(this.state.exchangeRate),
                markupPercentage: convertToNumber(this.state.markupPercentage),
                platformFeePercentage: convertToNumber(this.state.platformFeePercentage),
                platformFee: convertToNumber(this.state.platformFee),
                isMarkupActive: this.state.checkedFields.isMarkupActive ? 1 : 0,
                isPlatformPercentActive: this.state.checkedFields.isPlatformPercentActive ? 1 : 0,
                isPlatformFeeActive: this.state.checkedFields.isPlatformFeeActive ? 1 : 0
            }
            try {
                const response = await ExchanageRateService.addCurrencyPairs(this.state.accessToken, postData);
                if (response.ok) {
                    console.log("Data to be sent:", postData);
                    this.props.onClose?.(true, action);
                    ShowAlert(
                        "Success",
                        "Currency pair saved successfully! Kindly update the referral rules.",
                        "success"
                    ).then(() => {
                        this.props.refreshData();
                    });
                }
            } catch (error) {
                console.error("Error saving currency pair:", error);
                ShowAlert("Error", "Failed to save currency pair", "error");
            }
        } else this.props.onClose?.(false, action);
    };

    handleCheckboxChange = (field: string, checked: boolean) => {
        this.setState((prevState: Readonly<any>) => {
            const updatedState: any = {
                checkedFields: {
                    ...prevState.checkedFields,
                    [field]: checked,
                },
            };

            const errors = {
                fromCurrencyError: "",
                toCurrencyError: "",
                exchangeRateError: "",
                markupPercentageError: "",
                platformFeePercentageError: "",
                platformFeeError: ""
            };

            if (field === 'isMarkupActive' && !checked) {
                updatedState.markupPercentage = "";
                errors.markupPercentageError = "";
            }

            if (field === 'isPlatformPercentActive' && !checked) {
                updatedState.platformFeePercentage = "";
                errors.platformFeePercentageError = "";
            }

            if (field === 'isPlatformFeeActive' && !checked) {
                updatedState.platformFee = "";
                errors.platformFeeError = "";
            }
            this.setState(errors);
            return updatedState;
        });
    };

    render() {
        const { open, onClose } = this.props;
        const {
            fromCurrency,
            toCurrency,
            exchangeRate,
            markupPercentage,
            platformFeePercentage,
            platformFee,
            exchangeRateError,
            markupPercentageError,
            platformFeePercentageError,
            platformFeeError,
            fromCurrencyError,
            toCurrencyError,
            fromCurrenciesList,
            toCurrenciesList,
            loading,
            checkedFields
        } = this.state;

        const isSubmitDisabled = loading || !fromCurrency || !toCurrency ||
            !exchangeRate || !markupPercentage ||
            !platformFeePercentage || !platformFee;
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionComponent={SlideUpTransition} keepMounted>
                <DialogTitle>Add Currency Pair</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Grid container spacing={2}>
                            {/* From Currency Dropdown */}
                            <Grid item xs={12} sm={6}>
                                <FormLabel className="input-field-label">From currency</FormLabel>
                                <Autocomplete
                                    options={fromCurrenciesList}
                                    getOptionLabel={(option) => `${option.country} (${option.currency})`}
                                    value={fromCurrenciesList.find(currency => currency.currencyId === fromCurrency) || null}
                                    onChange={(_, newValue) => {
                                        if (newValue) {
                                            this.setState({
                                                fromCurrencyObj: newValue,
                                                fromCurrency: newValue.currencyId
                                            }, () => {
                                                this.handleFromCurrencyChange(newValue.currencyId);
                                            });
                                        } else {
                                            this.setState({
                                                fromCurrencyObj: null,
                                                fromCurrency: "",
                                                toCurrency: "",
                                                toCurrenciesList: []
                                            });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search"
                                            variant="outlined"
                                            fullWidth
                                            disabled={loading}
                                            error={!!fromCurrencyError}
                                            helperText={fromCurrencyError}
                                            color="secondary"
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <MenuItem {...props} key={option.currencyId}>
                                            {option.country} ({option.currency})
                                        </MenuItem>
                                    )}
                                    isOptionEqualToValue={(option, value) => option.currencyId === value.currencyId}
                                />
                            </Grid>

                            {/* To Currency Dropdown */}
                            <Grid item xs={12} sm={6}>
                                <FormLabel className="input-field-label">To currency</FormLabel>
                                <Autocomplete
                                    options={toCurrenciesList}
                                    getOptionLabel={(option) => `${option.country} (${option.currency})`}
                                    value={toCurrenciesList.find(currency => currency.currencyId === toCurrency) || null}
                                    onChange={(_, newValue) => {
                                        const value = newValue ? newValue.currencyId : '';
                                        const selectedCurrencyObj = this.state.toCurrenciesList.find(
                                            (item) => item.currencyId === value
                                        );
                                        if (selectedCurrencyObj) {
                                            this.setState({
                                                toCurrencyObj: selectedCurrencyObj,
                                                toCurrency: value
                                            });
                                        } else {
                                            this.setState({
                                                toCurrencyObj: null,
                                                toCurrency: ""
                                            });
                                        }
                                    }}
                                    disabled={loading || !fromCurrency}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            fullWidth
                                            error={!!toCurrencyError}
                                            helperText={toCurrencyError}
                                            placeholder={fromCurrency ? "Select target currency" : "Select source currency first"}
                                            color="secondary"
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <MenuItem {...props} key={option.currencyId}>
                                            {option.country} ({option.currency})
                                        </MenuItem>
                                    )}
                                    isOptionEqualToValue={(option, value) => option.currencyId === value.currencyId}
                                    noOptionsText="No currencies available"
                                />
                            </Grid>
                            {/* Exchange Rate */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Exchange Rate"
                                    placeholder="0.00"
                                    value={exchangeRate}
                                    startAdornment={this.state.toCurrencyObj?.currencySymbol}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('exchangeRate', value)}
                                    error={!!exchangeRateError}
                                    helperText={exchangeRateError}
                                    disabled={loading || this.state.toCurrencyObj! == null}
                                />
                            </Grid>

                            {/* Markup Percentage */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Markup Percentage"
                                    placeholder="0.00"
                                    value={markupPercentage}
                                    endAdornment={"%"}
                                    inputType={'text'}
                                    onChange={(value) => this.handleChange('markupPercentage', value)}
                                    error={!!markupPercentageError}
                                    helperText={markupPercentageError}
                                    checkedValue={checkedFields.isMarkupActive}
                                    onCheckboxChange={(checked) => this.handleCheckboxChange("isMarkupActive", checked)}
                                    disabled={!checkedFields.isMarkupActive || this.state.toCurrencyObj! == null}
                                />
                            </Grid>

                            {/* Platform Fee Percentage */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Platform Fee %"
                                    placeholder="0.00"
                                    value={platformFeePercentage}
                                    endAdornment={"%"}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('platformFeePercentage', value)}
                                    error={!!platformFeePercentageError}
                                    helperText={platformFeePercentageError}
                                    checkedValue={checkedFields.isPlatformPercentActive}
                                    onCheckboxChange={(checked) => this.handleCheckboxChange("isPlatformPercentActive", checked)}
                                    disabled={!checkedFields.isPlatformPercentActive || this.state.toCurrencyObj! == null}
                                />
                            </Grid>

                            {/* Platform Fee */}
                            <Grid item xs={12} sm={6}>
                                <InputTextField
                                    label="Platform Fee"
                                    placeholder="0.00"
                                    value={platformFee}
                                    startAdornment={"$"}
                                    inputType="number"
                                    onChange={(value) => this.handleChange('platformFee', value)}
                                    error={!!platformFeeError}
                                    helperText={platformFeeError}
                                    checkedValue={checkedFields.isPlatformFeeActive}
                                    onCheckboxChange={(checked) => this.handleCheckboxChange("isPlatformFeeActive", checked)}
                                    disabled={!checkedFields.isPlatformFeeActive || this.state.toCurrencyObj! == null}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => this.handleDialogAction("cancel")}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => this.handleDialogAction("ok")}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddCurrencyPairs;